import React, { useState, useEffect } from 'react';
import MobileCart from './MobileCart';
import TermsAndConditionsPopup from '../../components/TermsAndConditionsPopup';
import MobileOrderCompletionPopup from './MobileOrderCompletionPopup';
import { BACKEND_URL } from '../../config';
import './MobileCheckoutPage.css';

// Define currency symbols
const currencySymbols = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
  MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
};

const MobileCheckoutPage = ({
  cartItems: initialCartItems,
  removeFromCart,
  completeCheckout,
  cancelCheckout,
  goToMainPage,
  userSettings,
  username,
  calculatePrice,
  togglePaymentType,
  resetApp,
  clearCart,
  paymentSplitData
}) => {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localCartItems, setLocalCartItems] = useState(initialCartItems);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const isPriceSplitAllowed = userSettings?.priceSplit?.enabled !== false;
  const shouldUsePaymentSplit = isPriceSplitAllowed && paymentSplitData?.isEnabled;

  useEffect(() => {
    setLocalCartItems(initialCartItems);
  }, [initialCartItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAgreed) {
      setError('You must agree to the terms and conditions.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Create a local copy of orderNotes to collect additional notes
    let collectedOrderNotes = orderNotes;

    // Add payment split info to order notes if enabled
    if (shouldUsePaymentSplit) {
      const currencySymbol = currencySymbols[userSettings?.currency || 'USD'] || '$';
      const splitNote = `\n\n--- Payment Split ---\nCash: ${currencySymbol}${paymentSplitData.cashAmount.toFixed(2)} (${paymentSplitData.cashPercentage.toFixed(1)}%)\nCredit: ${currencySymbol}${paymentSplitData.creditAmount.toFixed(2)} (${paymentSplitData.creditPercentage.toFixed(1)}%)`;
      collectedOrderNotes = collectedOrderNotes ? collectedOrderNotes + splitNote : splitNote.trim();
    }

    const orderData = {
      customer_name: customerName,
      customer_email: customerEmail,
      termsAgreed,
      orderNotes: collectedOrderNotes,
      username,
      line_items: localCartItems.map(item => {
        const offeredPrice = item.isCash ? item.cashPrice : item.creditPrice;
        const lowPrice = item.originalBasePrice || item.basePrice;
        
        // Ensure skuId is a number or null
        let skuId = null;
        if (item.skuId !== undefined && item.skuId !== null) {
          skuId = Number(item.skuId);
          if (isNaN(skuId)) {
            console.warn(`Invalid skuId for item ${item.name}: ${item.skuId}`);
            skuId = null;
          }
        }

        // Collect any order notes from items
        if (item.orderNote && !collectedOrderNotes.includes(item.orderNote)) {
          if (collectedOrderNotes) {
            collectedOrderNotes += "\n" + item.orderNote;
          } else {
            collectedOrderNotes = item.orderNote;
          }
        }
        
        return {
          productId: item.productId,
          skuId: skuId,
          name: item.name,
          printType: item.printing,
          condition: item.condition,
          language: item.language || 'EN',
          quantity: item.quantity,
          offeredCashPrice: item.cashPrice,
          offeredCreditPrice: item.creditPrice,
          type: item.isCash ? 'Cash' : 'Credit',
          offeredPrice: offeredPrice,
          lowPrice: lowPrice,
          itemType: item.itemType,
          isSealed: item.isSealed === true,
          isSingle: item.isSingle === false ? false : undefined,
        };
      }),
    };

    try {
      const response = await fetch(`${BACKEND_URL}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      setOrderCompleted(true);
      setShowCompletionPopup(true);

      // Don't send ORDER_COMPLETED here - it will be sent when user closes the popup
    } catch (err) {
      console.error('Error submitting order:', err);
      setError(`An error occurred during checkout: ${err.message}. Please try again or contact support if the issue persists.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // handleUpdateQuantity removed - cart now uses separate line items

  const handleRemoveFromCart = (item) => {
    const updatedItems = localCartItems.filter(cartItem => cartItem.cartItemId !== item.cartItemId);
    setLocalCartItems(updatedItems);
    removeFromCart(item);
    
    window.parent.postMessage({ type: 'CART_UPDATED', cartItems: updatedItems }, '*');
  };

  const handleTogglePaymentType = (item) => {
    const updatedItems = localCartItems.map(cartItem =>
      cartItem.cartItemId === item.cartItemId
        ? { ...cartItem, isCash: !cartItem.isCash }
        : cartItem
    );
    setLocalCartItems(updatedItems);
    togglePaymentType(item);
    
    window.parent.postMessage({ type: 'CART_UPDATED', cartItems: updatedItems }, '*');
  };

  const openTermsAndConditions = (e) => {
    e.preventDefault();
    setShowTermsPopup(true);
  };

  // Note: quantity is always 1 now (separate line items)
  const calculateTotal = () => {
    return localCartItems.reduce((total, item) => {
      const price = item.isCash ? item.cashPrice : item.creditPrice;
      return total + price;
    }, 0);
  };

  const handleCompletionPopupClose = () => {
    setShowCompletionPopup(false);
    setCustomerName('');
    setCustomerEmail('');
    setOrderNotes('');
    setTermsAgreed(false);
    setLocalCartItems([]);
    setOrderCompleted(false);

    // Send ORDER_COMPLETED message to reset the app and clear cart
    window.parent.postMessage({ type: 'ORDER_COMPLETED' }, '*');
  };

  const toggleCartView = () => {
    setShowCart(!showCart);
  };

  const renderCheckoutContent = () => {
    if (orderCompleted) {
      return null;
    }
    return (
      <div className="mobile-checkout-page">
        <div className="mobile-checkout-header">
          <button onClick={goToMainPage} className="mobile-home-button">
            <span className="mobile-home-icon">&#8962;</span>
          </button>
          <div className="mobile-cart-icon">&#128722;</div>
        </div>
        
        {error && <div className="mobile-error" role="alert">{error}</div>}
        
        <div className="mobile-checkout-content">
          <div className="mobile-total">
            <span>Total:</span> <strong>{userSettings && userSettings.currency ?
              (currencySymbols[userSettings.currency] || '$') : '$'}
              {calculateTotal().toFixed(2)}</strong>
          </div>

          {/* Payment Split Summary */}
          {shouldUsePaymentSplit && (
            <div className="mobile-payment-split-summary">
              <h3>Payment Split</h3>
              <div className="mobile-split-details">
                <div className="mobile-split-row">
                  <span>Cash:</span>
                  <span>{currencySymbols[userSettings?.currency || 'USD'] || '$'}{paymentSplitData.cashAmount.toFixed(2)} ({paymentSplitData.cashPercentage.toFixed(1)}%)</span>
                </div>
                <div className="mobile-split-row">
                  <span>Credit:</span>
                  <span>{currencySymbols[userSettings?.currency || 'USD'] || '$'}{paymentSplitData.creditAmount.toFixed(2)} ({paymentSplitData.creditPercentage.toFixed(1)}%)</span>
                </div>
                <div className="mobile-split-row mobile-split-total">
                  <span>Split Total:</span>
                  <span>{currencySymbols[userSettings?.currency || 'USD'] || '$'}{(paymentSplitData.cashAmount + paymentSplitData.creditAmount).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="mobile-checkout-form">
            <h2>CUSTOMER INFORMATION</h2>
            <form onSubmit={handleSubmit}>
              <div className="mobile-form-group">
                <label htmlFor="customerName">Name</label>
                <input
                  id="customerName"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  placeholder="Enter your full name"
                  aria-required="true"
                />
              </div>
              <div className="mobile-form-group">
                <label htmlFor="customerEmail">Email</label>
                <input
                  id="customerEmail"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                  placeholder="Enter your email address"
                  aria-required="true"
                />
              </div>
              <div className="mobile-form-group">
                <label htmlFor="orderNotes">Order Notes (optional)</label>
                <textarea
                  id="orderNotes"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Any special instructions or comments?"
                />
              </div>
              <div className="mobile-form-group mobile-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                    required
                    aria-required="true"
                  />
                  <span>I agree to the <a href="#" onClick={openTermsAndConditions}>terms and conditions</a></span>
                </label>
              </div>
              <div className="mobile-button-group">
                <button type="submit" className="mobile-submit-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : 'SUBMIT ORDER'}
                </button>
              </div>
            </form>
          </div>
        </div>
        
        <div className="mobile-checkout-footer">
          <p>Secure checkout powered by <a href="https://tcgsync.com" target="_blank" rel="noopener noreferrer">TCGSync</a></p>
          <div className="mobile-security-badges">
            <span className="mobile-security-badge">SSL Secured</span>
            <span className="mobile-security-badge">256-bit Encryption</span>
          </div>
        </div>
        
        {showTermsPopup && (
          <TermsAndConditionsPopup
            termsAndConditions={userSettings.tncS}
            onClose={() => setShowTermsPopup(false)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {showCompletionPopup && (
        <MobileOrderCompletionPopup 
          onClose={handleCompletionPopupClose} 
          isProcessing={isSubmitting}
          orderCompleted={orderCompleted}
          onResetApp={() => window.parent.postMessage({ type: 'RESET_APP' }, '*')}
        />
      )}
      {renderCheckoutContent()}
    </>
  );
};

export default MobileCheckoutPage;
